import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  Input,
  Stack,
  Text,
  VStack,
  Tabs,
  TabList,
  Tab,
  Tag,
  TagLabel,
  TagCloseButton,
  IconButton,
} from '@chakra-ui/react';
import { AddIcon, ChevronDownIcon } from '@chakra-ui/icons';
import ProductsDropdown from '../../components/ProductsDropdown';
import { getSectorCategoryProductsApi } from '../../api/productApi';
import { useDispatch, useSelector } from 'react-redux';
import SectorCategoriesDropdown from '../../components/SectorCategoriesDropdown';
import { getSectorsCategories } from '../../actions/sector/sectorActions';
import { useToast } from '@chakra-ui/react';

const EnquiryManagement = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { loading: categoriesLoading, data: sectorData } = useSelector(
    (state) => state.sectorReducer
  );

  const [step, setStep] = useState(1);
  const [selectedSectorCategory, setSelectedSectorCategory] = useState(null);
  const [formData, setFormData] = useState({
    products: [],
    shippingLocation: '',
    deliveryStart: '',
    deliveryEnd: ''
  });

  useEffect(() => {
    dispatch(getSectorsCategories());
  }, [dispatch]);

  const handleSectorCategoryChange = (selectedCategory) => {
    setSelectedSectorCategory(selectedCategory?.value);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (formData.products.length < 3) {
      toast({
        title: "Minimum products required",
        description: "Please select at least 3 products",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!formData.shippingLocation) {
      toast({
        title: "Shipping location required",
        description: "Please enter a shipping location",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Submit the enquiry
    console.log("Enquiry submitted:", formData);
    toast({
      title: "Enquiry submitted",
      description: "Your enquiry has been submitted successfully",
      status: "success",
      duration: 5000,
      isClosable: true,
    });
  };

  return (
    <Box p={4} maxW="800px" mx="auto">
      {step === 1 ? (
        <VStack spacing={4}>
          <Text
            fontWeight="bold"
            fontSize="md"
            textAlign="center"
            textDecor="underline"
            cursor="pointer"
            onClick={() => setStep(2)}
          >
            Submit Product Enquiry Form
          </Text>

          <Flex align="center" gap={2} w="100%">
            <Divider />
            <Text fontSize="sm" whiteSpace="nowrap">OR</Text>
            <Divider />
          </Flex>

          <VStack spacing={2} align="start" w="100%">
            <Text fontWeight="bold" fontSize="sm">
              Order from our online stores
            </Text>
            <Flex gap={2}>
              <Box bg="gray.50" p={2} borderRadius="md" fontSize="xs">Amaz Logo</Box>
              <Text fontSize="xs">https://www.amazon.com/shops/sellerID</Text>
            </Flex>
            <Flex gap={2}>
              <Box bg="gray.50" p={2} borderRadius="md" fontSize="xs">Official</Box>
              <Text fontSize="xs">https://www.abc.com</Text>
            </Flex>
          </VStack>
        </VStack>
      ) : (
        <Box
          bg="white"
          borderRadius="lg"
          p={6}
          boxShadow="md"
          w="100%"
          maxW={{ base: '100%', sm: '90%', md: '80%', lg: '60%' }}
          mx="auto"
        >
          <Text fontWeight="bold" mb={1}>
            Submit Enquiry{' '}
            <Text as="span" fontWeight="normal" color="gray.500" fontSize="sm">
              to find more about your chosen SME/industry
            </Text>
          </Text>

          <Tabs variant="solid-rounded" colorScheme="gray" mt={2} mb={4}>
            <TabList>
              <Tab bg="black" color="white" _selected={{ bg: 'black' }}>Standard</Tab>
              <Tab>Custom</Tab>
            </TabList>
          </Tabs>

          <Stack spacing={4} mt={3} as="form" onSubmit={handleSubmit}>
            {/* Industry Category */}
            <Flex
              width={{ base: "100%", md: "auto" }}
              wrap={"wrap"}
              alignItems={"flex-start"}
              direction={{ base: "column", md: "row" }}
              justifyContent={{ base: "flex-start", md: "space-between" }}
            >
              <Text
                width={{ base: "100%", md: "9rem" }}
                pt={{ base: "1rem", md: "0" }}
                textAlign={"left"}
                ml={{ base: "0", md: "10px" }}
              >
                Industry Category
              </Text>

              <Box w={{ base: "100%", md: "21rem" }}>
                {categoriesLoading ? (
                  <p>Loading categories...</p>
                ) : (
                  <SectorCategoriesDropdown
                    sectorCategories={sectorData?.data}
                    selectedSectorCategory={selectedSectorCategory}
                    handleSectorCategoryChange={handleSectorCategoryChange}
                    name={"sectorcategory"}
                  />
                )}
              </Box>
            </Flex>

            {/* Products */}
            <Flex
              width={{ base: "100%", md: "auto" }}
              wrap={"wrap"}
              alignItems={"flex-start"}
              direction={{ base: "column", md: "row" }}
              justifyContent={{ base: "flex-start", md: "space-between" }}
            >
              <Text
                width={{ base: "100%", md: "9rem" }}
                pt={{ base: "1rem", md: "0" }}
                textAlign={"left"}
                ml={{ base: "0", md: "10px" }}
              >
                Products
              </Text>

              <Box w={{ base: "100%", md: "21rem" }}>
                <Text fontSize="14px" fontWeight={500} mb={1}>
                  {formData.products.length === 0
                    ? "Minimum 3 required/Maximum 20 available"
                    : formData.products.length < 3
                      ? "Minimum 3 required"
                      : "Maximum 20 available"}
                </Text>

                {selectedSectorCategory && (
                  <ProductsDropdown
                    defaultProductValues={formData.products}
                    isEditable={true}
                    selectedSectorCategory={selectedSectorCategory}
                    fetchDataFunction={getSectorCategoryProductsApi}
                    placeholder="Search Products..."
                    isMulti
                    handleOptionChange={(selectedOptions) => {
                      const products = selectedOptions.map((val) => val.value);
                      if (products.length <= 20) {
                        setFormData((prev) => ({
                          ...prev,
                          products,
                        }));
                      }
                    }}
                    _focus={{
                      height: "3rem",
                      borderLeft: "5px solid #30d5c8",
                    }}
                    name={"products"}
                  />
                )}
              </Box>
            </Flex>

            {/* Shipping location */}
            <Flex
              width={{ base: "100%", md: "auto" }}
              wrap={"wrap"}
              alignItems={"flex-start"}
              direction={{ base: "column", md: "row" }}
              justifyContent={{ base: "flex-start", md: "space-between" }}
            >
              <Text
                width={{ base: "100%", md: "9rem" }}
                pt={{ base: "1rem", md: "0" }}
                textAlign={"left"}
                ml={{ base: "0", md: "10px" }}
              >
                Shipping Location
              </Text>
              <Box w={{ base: "100%", md: "21rem" }}>
                <Input 
                  name="shippingLocation" 
                  value={formData.shippingLocation}
                  onChange={handleChange}
                  placeholder="Enter shipping location" 
                  size="sm" 
                  bg="gray.100" 
                />
              </Box>
            </Flex>

            {/* Delivery timeline */}
            <Flex
              width={{ base: "100%", md: "auto" }}
              wrap={"wrap"}
              alignItems={"flex-start"}
              direction={{ base: "column", md: "row" }}
              justifyContent={{ base: "flex-start", md: "space-between" }}
            >
              <Text
                width={{ base: "100%", md: "9rem" }}
                pt={{ base: "1rem", md: "0" }}
                textAlign={"left"}
                ml={{ base: "0", md: "10px" }}
              >
                Delivery Timeline
              </Text>
              <Box w={{ base: "100%", md: "21rem" }}>
                <Flex gap={2} alignItems="center">
                  <Input 
                    type="date" 
                    name="deliveryStart"
                    value={formData.deliveryStart}
                    onChange={handleChange}
                    size="sm" 
                  />
                  <Text fontSize="sm">to</Text>
                  <Input 
                    type="date" 
                    name="deliveryEnd"
                    value={formData.deliveryEnd}
                    onChange={handleChange}
                    size="sm" 
                  />
                </Flex>
              </Box>
            </Flex>

            <Button
              mt={4}
              type="submit"
              colorScheme="gray"
              bg="#5D5D5D"
              color="white"
              w={{ base: "100%", sm: "auto" }}
              px={6}
              isLoading={false}
            >
              Submit Enquiry
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default EnquiryManagement;